import type { ComponentPropsWithRef, ReactElement, Ref } from 'react'
import { forwardRef } from 'react'

import type { UseComboboxGetItemPropsOptions } from 'downshift'

import type { SelectOption } from '@subaio/components/src/HookForm'

import { Anchor } from './Anchor'
import { CheckMark } from './CheckMark'
import { Container } from './Container'
import { Empty } from './Empty'
import { Item } from './Item'
import { ItemContent } from './ItemContent'
import { Label } from './Label'

type Props<TFieldValues = string | number | boolean> = ComponentPropsWithRef<'div'> & {
	highlightedOptionIndex: number
	isLoading: boolean
	isOpen: boolean
	maxNumberOfOptionsVisible: number
	options: Array<SelectOption<TFieldValues>>
	selectedValues: Array<TFieldValues>

	getItemProps: (options: UseComboboxGetItemPropsOptions<TFieldValues>) => Record<string, unknown>
	onSelectOption?: (item: TFieldValues) => void
}

const Menu = <TFieldValues = string | number | boolean>(
	{
		getItemProps,
		highlightedOptionIndex,
		isLoading,
		isOpen,
		maxNumberOfOptionsVisible,
		onSelectOption,
		options,
		selectedValues,
	}: Props<TFieldValues>,
	ref?: Ref<HTMLDivElement>,
) => (
	<Anchor>
		<Container ref={ref} isOpen={isOpen}>
			{options.length === 0 ? (
				<Empty>{isLoading ? 'Loading your options...' : 'Nothing matches your search'}</Empty>
			) : (
				options.slice(0, maxNumberOfOptionsVisible).map((item, index: number) => (
					<Item
						{...(getItemProps({
							key: `${item.label}-${index}`,
							index,
							item: item.value,
							onClick: onSelectOption && !item.isDisabled ? () => onSelectOption(item.value) : undefined,
						}) as Record<string, unknown>)}
						key={index}
						isDisabled={item.isDisabled ?? false}
					>
						<ItemContent
							isHighlighted={highlightedOptionIndex === index}
							isSelected={selectedValues.some((value) => value === item.value)}
							isDisabled={item.isDisabled ?? false}
						>
							<Label>{item.render ? item.render(item) : item.label}</Label>
							<CheckMark isChecked={selectedValues.some((value) => value === item.value)} />
						</ItemContent>
					</Item>
				))
			)}
		</Container>
	</Anchor>
)

export const SelectMenu = forwardRef(Menu) as <TFieldValues = string | number | boolean>(
	props: Props<TFieldValues> & { ref?: Ref<HTMLDivElement> },
) => ReactElement
